import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, Eye, ToggleLeft } from "lucide-react";
import api, { humanError } from "@/lib/api";

/**
 * Operator tools for the platform console.
 *
 * Feature overrides and impersonation both existed with nothing to reach them,
 * which meant the only way to grant a trial customer a feature was to edit the
 * database by hand -- exactly the thing feature flags are meant to replace.
 */
export default function PlatformTools({ tenants, onChanged }) {
  const [catalogue, setCatalogue] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [selected, setSelected] = useState("");
  const [flags, setFlags] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/admin/features").then(({ data }) => setCatalogue(data)).catch(() => setCatalogue(null));
    api.get("/metrics/operational").then(({ data }) => setMetrics(data)).catch(() => setMetrics(null));
  }, []);

  async function loadFlags(tenantId) {
    setSelected(tenantId);
    setFlags([]);
    if (!tenantId) return;
    try {
      // The tenant-facing endpoint already returns the resolved answer per
      // flag, including whether an override is doing the work, so there is no
      // second shape to keep in step.
      const { data } = await api.get("/tenants/features", {
        headers: { "X-Tenant-Id": tenantId },
      });
      setFlags(data.items || []);
    } catch (e) {
      toast.error(humanError(e));
    }
  }

  async function setOverride(key, enabled) {
    setBusy(key);
    try {
      await api.patch(`/admin/tenants/${selected}/features`, { overrides: { [key]: enabled } });
      toast.success(`${key} ${enabled ? "granted" : "withdrawn"}`);
      await loadFlags(selected);
      onChanged?.();
    } catch (e) {
      toast.error(humanError(e));
    } finally {
      setBusy("");
    }
  }

  async function clearOverride(key) {
    setBusy(key);
    try {
      await api.delete(`/admin/tenants/${selected}/features/${key}`);
      toast.success(`${key} follows the plan again`);
      await loadFlags(selected);
    } catch (e) {
      toast.error(humanError(e));
    } finally {
      setBusy("");
    }
  }

  async function impersonate() {
    if (reason.trim().length < 10) {
      toast.error("Give a reason of at least ten characters. It is written to the customer's audit log.");
      return;
    }
    setBusy("impersonate");
    try {
      const { data } = await api.post(`/admin/tenants/${selected}/impersonate`, { reason });
      // Deliberately not stored over the operator's own session: the grant is
      // read-only and expires, and clobbering their token would sign them out
      // of the console they are working in.
      await navigator.clipboard?.writeText(data.token).catch(() => {});
      toast.success(
        `Read-only access to ${data.tenant.name} for ${data.expires_in_minutes} minutes. Token copied.`
      );
      setReason("");
    } catch (e) {
      toast.error(humanError(e));
    } finally {
      setBusy("");
    }
  }

  const tenantName = tenants?.find((t) => t.id === selected)?.name;

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="platform-metrics">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#0B2B33]">
          <Activity className="h-4 w-4 text-[#1F5FD8]" /> System health
        </div>
        {metrics ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Dead letters", metrics.dead_letter, metrics.dead_letter > 0],
              ["Overdue jobs", metrics.queue_overdue, metrics.queue_overdue > 0],
              ["Publisher", metrics.publisher_running ? "running" : "stopped", !metrics.publisher_running],
              ["Storage", metrics.storage_backend, false],
            ].map(([label, value, bad]) => (
              <div key={label} className="rounded-lg bg-[#F6F8FA] p-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">{label}</div>
                <div className={`mt-1 font-display text-xl font-bold tabular-nums ${bad ? "text-[#B02328]" : "text-[#0B2B33]"}`}>
                  {value}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#8A97A0]">Metrics unavailable.</p>
        )}
      </section>

      <section className="rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="platform-flags">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#0B2B33]">
          <ToggleLeft className="h-4 w-4 text-[#1F5FD8]" /> Features and support access
        </div>

        <select
          value={selected}
          onChange={(e) => loadFlags(e.target.value)}
          data-testid="platform-tenant-select"
          className="w-full max-w-sm rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm"
        >
          <option value="">Choose a workspace…</option>
          {(tenants || []).map((t) => (
            <option key={t.id} value={t.id}>{t.name} — {t.plan_code}</option>
          ))}
        </select>

        {selected && flags.length ? (
          <>
            <ul className="mt-4 divide-y divide-[#E4E9EF]">
              {flags.map((f) => (
                <li key={f.key} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#0B2B33]">
                      {f.name}
                      {f.overridden ? (
                        <span className="ml-2 hs-chip bg-[#FFF4E2] text-[#B26A00]">override</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-[#8A97A0]">
                      {f.in_plan ? "Included in their plan" : "Not in their plan"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setOverride(f.key, !f.enabled)}
                      disabled={busy === f.key}
                      data-testid={`flag-toggle-${f.key}`}
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        f.enabled
                          ? "bg-[#E8FBF1] text-[#16A46B]"
                          : "border border-[#E4E9EF] text-[#43535E] hover:bg-[#F6F8FA]"
                      }`}
                    >
                      {f.enabled ? "On" : "Off"}
                    </button>
                    {f.overridden ? (
                      <button onClick={() => clearOverride(f.key)} disabled={busy === f.key}
                        className="text-xs font-semibold text-[#1F5FD8] hover:underline">
                        Reset
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-5 rounded-lg bg-[#F6F8FA] p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#0B2B33]">
                <Eye className="h-4 w-4" /> Open {tenantName} read-only
              </div>
              <p className="mt-1 text-xs text-[#43535E]">
                Thirty minutes, read-only, and written to their audit log with the reason you give.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why are you opening this workspace?"
                  data-testid="impersonate-reason"
                  className="min-w-[260px] flex-1 rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm"
                />
                <button onClick={impersonate} disabled={busy === "impersonate"}
                  data-testid="impersonate-start"
                  className="hs-btn-primary text-sm disabled:opacity-60">
                  {busy === "impersonate" ? "Opening…" : "Open read-only"}
                </button>
              </div>
            </div>
          </>
        ) : selected ? (
          <p className="mt-4 text-sm text-[#8A97A0]">No flags returned for that workspace.</p>
        ) : null}

        {catalogue ? (
          <p className="mt-4 text-xs text-[#8A97A0]">
            {catalogue.items.length} features defined across {Object.keys(catalogue.plans).length} plans.
          </p>
        ) : null}
      </section>
    </div>
  );
}
